/**
 * QualificationsModal — Gestion des qualifications professionnelles d'un membre.
 *
 * - Liste les qualifications du référentiel BATICO (Qualibat, Qualifelec, RGE, etc.)
 * - Permet d'uploader un ou plusieurs justificatifs (PDF/PNG/JPEG) par qualification
 * - Workflow Super Admin / Admin : approuver / rejeter avec motif
 * - Téléchargement, visualisation et suppression des justificatifs
 *
 * Backend : /api/admin/membres/{id}/qualifications/*
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Award, Upload, X, Clock, CheckCircle2, Calendar, Loader2, AlertTriangle, MessageSquare,
  Scale, Ban, RotateCw, FileSignature, QrCode,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { StatusBadge, JustificatifRow } from './qualificationsShared';

const API = process.env.REACT_APP_BACKEND_URL;

/** Composant principal : modal pour gérer toutes les qualifications d'un membre */
export const QualificationsModal = ({ membre, open, onClose, token, userRole }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [validUntilByKey, setValidUntilByKey] = useState({});
  const [arbitrage, setArbitrage] = useState(null);
  const [arbitrageStatus, setArbitrageStatus] = useState('none');
  const [revoking, setRevoking] = useState(false);
  const fileInputsRef = useRef({});

  const canReview = ['super_admin', 'admin'].includes(userRole);

  const fetchArbitrage = useCallback(async () => {
    if (!membre?.id || !token || !canReview) return;
    try {
      const res = await axios.get(
        `${API}/api/admin/members/${membre.id}/arbitrage`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArbitrage(res.data.arbitrage);
      setArbitrageStatus(res.data.status || 'none');
    } catch {
      setArbitrage(null);
      setArbitrageStatus('none');
    }
  }, [membre?.id, token, canReview]);

  const handleRevokeArbitrage = async () => {
    if (!window.confirm(
      `Révoquer l'arbitrage RCDS de ce membre ?\n\nIl devra repasser par le scoring normal lors de sa prochaine simulation et pourra retomber en VALIDATION_MANUELLE.`
    )) return;
    const reason = window.prompt('Motif de la révocation (optionnel) :', '');
    setRevoking(true);
    try {
      await axios.post(
        `${API}/api/admin/members/${membre.id}/revoke-arbitrage`,
        { reason: (reason || '').trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Arbitrage révoqué — le membre repasse au scoring normal');
      fetchArbitrage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la révocation');
    } finally {
      setRevoking(false);
    }
  };

  const handleRenewArbitrage = async () => {
    const rawMonths = window.prompt(
      `Renouveler l'arbitrage RCDS — durée en mois (1 à 60) ?\n\nCoefficient libre conservé : ×${Number(arbitrage?.coefficient_libre || 0).toFixed(2)}`,
      '12'
    );
    if (rawMonths == null) return;
    const months = parseInt(String(rawMonths).trim(), 10);
    if (!months || months < 1 || months > 60) {
      toast.error('Durée invalide (1 à 60 mois)');
      return;
    }
    setRevoking(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/members/${membre.id}/renew-arbitrage`,
        { valid_until_months: months, comment: 'Renouvellement 1-clic' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Arbitrage renouvelé ${months} mois (×${res.data.renewal_count})`,
      );
      fetchArbitrage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du renouvellement');
    } finally {
      setRevoking(false);
    }
  };

  // Génération du Relevé d'information RCDS (PDF + QR JWT RS256)
  const [generatingReleve, setGeneratingReleve] = useState(false);
  const handleGenerateReleve = async () => {
    if (!membre?.id) return;
    setGeneratingReleve(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/members/${membre.id}/releve-information/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Relevé ${res.data.releve_id} généré`);
      const r2 = await axios.get(res.data.download_url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r2.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.releve_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingReleve(false);
    }
  };


  const fetchItems = useCallback(async () => {
    if (!membre?.id || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/membres/${membre.id}/qualifications`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(res.data.qualifications || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger les qualifications');
    } finally {
      setLoading(false);
    }
  }, [membre?.id, token]);

  useEffect(() => {
    if (open && membre?.id) {
      fetchItems();
      fetchArbitrage();
    }
  }, [open, membre?.id, fetchItems, fetchArbitrage]);

  const handleFileSelected = async (qualifKey, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!allowed.includes(file.type)) {
      toast.error('Seuls les fichiers PDF, PNG et JPEG sont autorisés');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (20 Mo max)');
      return;
    }

    setUploadingKey(qualifKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const vu = validUntilByKey[qualifKey];
      if (vu) formData.append('valid_until', vu);

      await axios.post(
        `${API}/api/admin/membres/${membre.id}/qualifications/${qualifKey}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      toast.success('Justificatif déposé — en attente de validation');
      // Reset l'input
      if (fileInputsRef.current[qualifKey]) {
        fileInputsRef.current[qualifKey].value = '';
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingKey(null);
    }
  };

  const pendingCount = items.filter(
    (it) => it.documents.some((d) => d.status === 'pending'),
  ).length;
  const approvedCount = items.filter((it) => it.status === 'approved').length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="qualifications-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Award className="h-5 w-5 text-amber-500" />
            Qualifications professionnelles
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <span className="text-slate-600">
              {membre?.entreprise || `${membre?.prenom || ''} ${membre?.nom || ''}`.trim()}
            </span>
            <Badge variant="outline" className="text-[10px]">
              {approvedCount} validée(s)
            </Badge>
            {pendingCount > 0 && (
              <Badge className="text-[10px] bg-amber-600 text-white border border-amber-800 font-semibold">
                {pendingCount} en attente
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* ============ Action : Générer le Relevé d'information RCDS ============ */}
        {canReview && membre?.id && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200">
            <FileSignature className="h-5 w-5 text-sky-700 flex-shrink-0" />
            <div className="flex-1 text-xs text-slate-700">
              <strong>Relevé d'information RCDS</strong> — synthèse adhésion + activités + avenants + état financier, signé QR (JWT RS256).
            </div>
            <Button
              size="sm"
              onClick={handleGenerateReleve}
              disabled={generatingReleve}
              className="bg-sky-600 hover:bg-sky-700 text-white gap-1.5 h-8"
              data-testid="generate-releve-btn"
            >
              {generatingReleve ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
              Générer le PDF
            </Button>
          </div>
        )}

        {/* ============ Encart Arbitrage RCDS actif ============ */}
        {canReview && arbitrage && arbitrageStatus !== 'none' && (
          <div
            className={`mt-2 rounded-xl border-2 p-4 ${
              arbitrageStatus === 'active'
                ? 'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border-violet-300'
                : 'bg-gradient-to-br from-slate-50 to-amber-50 border-amber-300'
            }`}
            data-testid="arbitrage-card"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                  arbitrageStatus === 'active' ? 'bg-gradient-to-br from-violet-500 to-fuchsia-600' : 'bg-gradient-to-br from-amber-500 to-orange-600'
                }`}>
                  <Scale className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span className={`text-xs font-bold uppercase tracking-widest ${
                    arbitrageStatus === 'active' ? 'text-violet-800' : 'text-amber-800'
                  }`}>
                    ⚖️ Arbitrage RCDS {arbitrageStatus === 'active' ? 'actif' : 'expiré'}
                  </span>
                  <div className="text-lg font-bold text-slate-800 mt-0.5">
                    Coefficient libre : <span className="font-mono">×{Number(arbitrage.coefficient_libre).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5">
                <Button
                  onClick={handleRenewArbitrage}
                  disabled={revoking}
                  size="sm"
                  variant="outline"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800 gap-1.5"
                  data-testid="renew-arbitrage-btn"
                >
                  {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                  Renouveler
                </Button>
                <Button
                  onClick={handleRevokeArbitrage}
                  disabled={revoking}
                  size="sm"
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 gap-1.5"
                  data-testid="revoke-arbitrage-btn"
                >
                  {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                  Révoquer
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-slate-700 bg-white/60 rounded-lg p-2.5 mt-2">
              {arbitrage.approved_at && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-slate-400" />
                  Approuvé le <strong>{new Date(arbitrage.approved_at).toLocaleDateString('fr-FR')}</strong>
                </div>
              )}
              {arbitrage.approved_by && (
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-slate-400" />
                  Par <strong className="truncate">{arbitrage.approved_by}</strong>
                </div>
              )}
              {arbitrage.valid_until && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-slate-400" />
                  Valide jusqu'au <strong>{new Date(arbitrage.valid_until).toLocaleDateString('fr-FR')}</strong>
                </div>
              )}
              {arbitrage.comment && (
                <div className="col-span-full flex items-start gap-1.5">
                  <MessageSquare className="h-3 w-3 text-slate-400 mt-0.5" />
                  <span className="italic">"{arbitrage.comment}"</span>
                </div>
              )}
            </div>
            {arbitrageStatus === 'expired' && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded-lg p-2 flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  Cet arbitrage est <strong>expiré</strong>. Le membre repasse au scoring normal lors de sa prochaine simulation.
                </span>
                <Button
                  onClick={handleRenewArbitrage}
                  disabled={revoking}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 h-7"
                  data-testid="renew-expired-btn"
                >
                  {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                  Renouveler en 1 clic
                </Button>
              </div>
            )}
            {(arbitrage.renewal_count ?? 0) > 0 && (
              <div className="mt-1.5 text-[10px] text-slate-500 italic">
                ↻ Renouvelé {arbitrage.renewal_count} fois
                {arbitrage.renewed_at && (
                  <> — dernier le {new Date(arbitrage.renewed_at).toLocaleDateString('fr-FR')}</>
                )}
                {arbitrage.renewed_by && (
                  <> par {arbitrage.renewed_by}</>
                )}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {items.map((it) => (
              <div
                key={it.key}
                className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
                data-testid={`qualif-card-${it.key}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-amber-500" />
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">{it.label}</div>
                      {it.coef !== undefined && (
                        <div className="text-[10px] text-slate-500">
                          coef tarifaire : ×{it.coef?.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={it.status} />
                </div>

                {/* Liste des documents */}
                {it.documents.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {it.documents.map((d) => (
                      <JustificatifRow
                        key={d.id}
                        doc={d}
                        membreId={membre.id}
                        token={token}
                        canReview={canReview}
                        onChanged={fetchItems}
                      />
                    ))}
                  </div>
                )}

                {/* Zone upload */}
                {canReview && (
                  <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-200">
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-[10px] text-slate-500">
                        Date de fin de validité (optionnelle)
                      </Label>
                      <DatePickerFr
                        value={validUntilByKey[it.key] || ''}
                        onChange={(v) =>
                          setValidUntilByKey({ ...validUntilByKey, [it.key]: v })
                        }
                        className="h-8 text-xs"
                        data-testid={`valid-until-${it.key}`}
                      />
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="application/pdf,image/png,image/jpeg"
                        ref={(el) => (fileInputsRef.current[it.key] = el)}
                        onChange={(e) => handleFileSelected(it.key, e.target.files?.[0])}
                        className="hidden"
                        data-testid={`upload-input-${it.key}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputsRef.current[it.key]?.click()}
                        disabled={uploadingKey === it.key}
                        className="h-8 gap-1.5 border-slate-300"
                        data-testid={`upload-btn-${it.key}`}
                      >
                        {uploadingKey === it.key ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3" />
                        )}
                        Déposer un justificatif
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {items.length === 0 && !loading && (
              <div className="text-center text-slate-500 py-8 text-sm">
                Aucune qualification configurée dans le référentiel BATICO.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="qualifs-close">
            <X className="h-4 w-4 mr-1" /> Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QualificationsModal;

